import { z } from 'zod';

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ListResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export function listResultSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    items: z.array(itemSchema),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
  });
}

export interface Helpers {
  request: (method: Method, path: string, body?: unknown) => Promise<unknown>;
  requestMultipart: (path: string, form: FormData) => Promise<unknown>;
  requestBlob: (path: string) => Promise<Blob>;
}

export function toQuery(obj: Record<string, unknown> | undefined): string {
  if (!obj) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null) continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// CRUD canônico (GET list / GET :id / POST / PATCH :id / DELETE :id) com validação
// zod nos dois sentidos. Resources expõem os métodos sob seus nomes públicos via alias.
export function makeCrudResource<
  TItem extends z.ZodTypeAny,
  TList extends z.ZodTypeAny,
  TCreate extends z.ZodTypeAny,
  TUpdate extends z.ZodTypeAny,
>(
  request: Helpers['request'],
  cfg: {
    basePath: string;
    schema: TItem;
    listQuerySchema: TList;
    createSchema: TCreate;
    updateSchema: TUpdate;
  },
) {
  const listSchema = listResultSchema(cfg.schema);
  const itemPath = (id: string) => `${cfg.basePath}/${encodeURIComponent(id)}`;
  return {
    async list(query?: z.input<TList>): Promise<ListResult<z.output<TItem>>> {
      const parsed = cfg.listQuerySchema.parse(query ?? {}) as Record<string, unknown>;
      return listSchema.parse(
        await request('GET', `${cfg.basePath}${toQuery(parsed)}`),
      ) as ListResult<z.output<TItem>>;
    },
    async get(id: string): Promise<z.output<TItem>> {
      return cfg.schema.parse(await request('GET', itemPath(id))) as z.output<TItem>;
    },
    async create(input: z.input<TCreate>): Promise<z.output<TItem>> {
      const parsed = cfg.createSchema.parse(input);
      return cfg.schema.parse(await request('POST', cfg.basePath, parsed)) as z.output<TItem>;
    },
    async update(id: string, input: z.input<TUpdate>): Promise<z.output<TItem>> {
      const parsed = cfg.updateSchema.parse(input);
      return cfg.schema.parse(await request('PATCH', itemPath(id), parsed)) as z.output<TItem>;
    },
    async delete(id: string): Promise<void> {
      await request('DELETE', itemPath(id));
    },
  };
}
